import React, { useState } from 'react';
import styled from '@emotion/styled';

const Section = styled.section`
  padding: 100px 24px;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 60px 16px;
  }

  @media (max-width: 480px) {
    padding: 40px 12px;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 42px;
  font-weight: 700;
  color: #0b0f1a;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 26px;
    margin-bottom: 12px;
  }
`;

const Subtitle = styled.p`
  font-family: 'Segoe UI', sans-serif;
  font-size: 18px;
  color: #4b5563;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FAQItem = styled.div<{ isOpen: boolean }>`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: ${props => props.isOpen ? '#f9fafb' : '#ffffff'};

  &:hover {
    border-color: #d1d5db;
  }
`;

const Question = styled.button`
  width: 100%;
  text-align: left;
  padding: 24px;
  background: none;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #0b0f1a;

  @media (max-width: 480px) {
    padding: 16px;
    font-size: 15px;
    gap: 12px;
  }
`;

const Icon = styled.span<{ isOpen: boolean }>`
  transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
  transition: transform 0.3s ease;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const Answer = styled.div<{ isOpen: boolean }>`
  padding: ${props => props.isOpen ? '0 24px 24px' : '0 24px'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  font-family: 'Segoe UI', sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #4b5563;
  opacity: ${props => props.isOpen ? 1 : 0};

  @media (max-width: 480px) {
    padding: ${props => props.isOpen ? '0 16px 16px' : '0 16px'};
    font-size: 14px;
    line-height: 1.5;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-bottom: 20px;
    gap: 8px;
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border-radius: 100px;
  border: none;
  background: ${props => props.active ? '#0b0f1a' : '#f3f4f6'};
  color: ${props => props.active ? '#ffffff' : '#4b5563'};
  font-family: 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#0b0f1a' : '#e5e7eb'};
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const faqData = {
    pricing: [
        {
            q: "Can I change my plan later?",
            a: "Yes, absolutely. You can upgrade or downgrade your plan at any time from your dashboard. If you upgrade, the change is immediate. If you downgrade, it takes effect at the end of your current billing cycle."
        },
        {
            q: "Do you offer a free demo?",
            a: "Yes. On request, we can build a limited preview using information from your public website. It is not a free plan or trial, requires no payment details, and creates no obligation to subscribe. Setup and onboarding are included if you choose a paid plan."
        },
        {
            q: "What payment methods do you accept?",
            a: "Payments are processed securely through Lemon Squeezy. The payment methods available for your location are shown during checkout."
        },
        {
            q: "Do you offer discounts for non-profits?",
            a: "Yes! We love supporting non-profits and educational institutions. Contact our sales team with proof of your status, and we'll set you up with a special discount."
        }
    ],
    product: [
        {
            q: "How does the AI training work?",
            a: "It's simple. You just provide your website URL or upload documents (PDFs, Word docs, etc.). Our AI scans this content to understand your business, products, and policies. It usually takes just a few minutes."
        },
        {
            q: "Can I customize the chatbot's look?",
            a: "Yes, you can fully customize the colors, welcome message, and icon to match your brand. On Pro and Unlimited plans, you can also remove the 'Powered by SmushChat' branding."
        },
        {
            q: "What happens if the AI doesn't know the answer?",
            a: "You can set up a fallback message or, on higher tier plans, enable human handoff. This notifies your team so a human can step in and take over the conversation seamlessly."
        }
    ],
    technical: [
        {
            q: "How do I install it on my website?",
            a: "We provide a small snippet of code (just like Google Analytics) that you copy and paste into your website's header. We also have plugins for WordPress, Shopify, and other major platforms."
        },
        {
            q: "Is my data secure?",
            a: "We use technical and organizational safeguards appropriate to the service and explain our data practices in the Privacy Policy. If you have a specific regulatory, retention, or security requirement, contact us before purchasing so we can confirm whether we can support it."
        }
    ]
};

const ComprehensiveFAQ: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'pricing' | 'product' | 'technical'>('pricing');
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const toggleFAQ = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <Section>
            <Container>
                <Header>
                    <Title>Frequently Asked Questions</Title>
                    <Subtitle>Everything you need to know about our pricing and platform.</Subtitle>
                </Header>

                <CategoryTabs>
                    <Tab active={activeTab === 'pricing'} onClick={() => setActiveTab('pricing')}>Pricing & Billing</Tab>
                    <Tab active={activeTab === 'product'} onClick={() => setActiveTab('product')}>Product Features</Tab>
                    <Tab active={activeTab === 'technical'} onClick={() => setActiveTab('technical')}>Technical & Setup</Tab>
                </CategoryTabs>

                <FAQList>
                    {faqData[activeTab].map((item, index) => (
                        <FAQItem key={index} isOpen={openIndex === index}>
                            <Question onClick={() => toggleFAQ(index)}>
                                {item.q}
                                <Icon isOpen={openIndex === index}>
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 1L6 6L11 1" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Icon>
                            </Question>
                            <Answer isOpen={openIndex === index}>
                                {item.a}
                            </Answer>
                        </FAQItem>
                    ))}
                </FAQList>
            </Container>
        </Section>
    );
};

export default ComprehensiveFAQ;
